/**
 * NOTIFICATION-CENTER-UX-D1-R1 — frontend-owned notification presentation.
 *
 * Keyed by exact `notification.type`. Localizes system-owned boilerplate while
 * preserving useful already-serialized recipient context. Not an authorization
 * source. Backend action resolution remains authoritative.
 */

import { DEAL_STATUS_LABELS } from "../../deals/lib/dealStatusLabels";
import {
  boundedText,
  extractAfterExactPrefix,
  formatAggregateContext,
  formatUkrainianDateTime,
  joinSupportingParts,
  safeAggregateCount,
  safePayload,
} from "./presentationContextHelpers";

export const NOTIFICATION_DOMAIN = {
  DEALS: "deals",
  CLIENTS: "clients",
  TASKS: "tasks",
  VIEWINGS: "viewings",
  COMPANIES: "companies",
  PROPERTY: "property",
  COMPLAINTS: "complaints",
  SUBSCRIPTION: "subscription",
  CHAT: "chat",
  PROFILE: "profile",
  GENERIC: "generic",
};

const DOMAIN_VISUALS = {
  [NOTIFICATION_DOMAIN.DEALS]: {
    icon: "fa-solid fa-handshake",
    tone: "deals",
    label: "Угода",
  },
  [NOTIFICATION_DOMAIN.CLIENTS]: {
    icon: "fa-solid fa-user-group",
    tone: "clients",
    label: "Клієнт",
  },
  [NOTIFICATION_DOMAIN.TASKS]: {
    icon: "fa-solid fa-list-check",
    tone: "tasks",
    label: "Задача",
  },
  [NOTIFICATION_DOMAIN.VIEWINGS]: {
    icon: "fa-solid fa-calendar-check",
    tone: "viewings",
    label: "Перегляд",
  },
  [NOTIFICATION_DOMAIN.COMPANIES]: {
    icon: "fa-solid fa-building",
    tone: "companies",
    label: "Компанія",
  },
  [NOTIFICATION_DOMAIN.PROPERTY]: {
    icon: "fa-solid fa-house",
    tone: "property",
    label: "Обʼєкт",
  },
  [NOTIFICATION_DOMAIN.COMPLAINTS]: {
    icon: "fa-solid fa-flag",
    tone: "complaints",
    label: "Скарга",
  },
  [NOTIFICATION_DOMAIN.SUBSCRIPTION]: {
    icon: "fa-solid fa-credit-card",
    tone: "subscription",
    label: "Підписка",
  },
  [NOTIFICATION_DOMAIN.CHAT]: {
    icon: "fa-solid fa-comment",
    tone: "chat",
    label: "Чат",
  },
  [NOTIFICATION_DOMAIN.PROFILE]: {
    icon: "fa-solid fa-user",
    tone: "profile",
    label: "Профіль",
  },
  [NOTIFICATION_DOMAIN.GENERIC]: {
    icon: "fa-solid fa-bell",
    tone: "generic",
    label: "Сповіщення",
  },
};

const FALLBACK_BODY = "Відкрийте Рієстро, щоб переглянути деталі.";
const DEAD_BODY = "Повʼязаний обʼєкт більше недоступний.";
const FOLLOWER_GENERIC_BODY = "Хтось підписався на ваш профіль.";

/** Conservative email-like guard for follower presentation (R1-B4). */
function looksLikeEmail(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 254) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}

/**
 * Follower-safe display label. Never renders sender.email or email-like
 * fullname fallbacks from UserShortSerializer.
 */
export function followerSafeDisplayName(sender) {
  if (!sender || typeof sender !== "object" || Array.isArray(sender)) {
    return "";
  }
  const fullname = boundedText(sender.fullname);
  if (fullname && !looksLikeEmail(fullname)) {
    return fullname;
  }
  const handle = boundedText(sender.handle);
  if (handle && !looksLikeEmail(handle)) {
    return handle;
  }
  return "";
}

function serializedTitle(notification) {
  return boundedText(notification && notification.title);
}

function serializedBody(notification) {
  return boundedText(notification && notification.body);
}

function withAggregate(body, notification) {
  const count = safeAggregateCount(safePayload(notification));
  return joinSupportingParts([body, formatAggregateContext(count)]);
}

function entry(domain, title, body) {
  return { domain, title, body };
}

function companyFromInvitationTitle(notification) {
  return extractAfterExactPrefix(serializedTitle(notification), "Invitation to ");
}

function companyFromJoinRequestTitle(notification) {
  return extractAfterExactPrefix(serializedTitle(notification), "Join request for ");
}

/** Exact production NotificationType values from current main + completion types. */
export const NOTIFICATION_PRESENTATION_BY_TYPE = {
  "company.invitation.received": entry(
    NOTIFICATION_DOMAIN.COMPANIES,
    (n) => {
      const company = companyFromInvitationTitle(n);
      return company
        ? `Запрошення до компанії «${company}»`
        : "Отримано запрошення до компанії";
    },
    (n) => serializedBody(n) || "Перегляньте запрошення в профілі компанії.",
  ),
  "company.invitation.accepted": entry(
    NOTIFICATION_DOMAIN.COMPANIES,
    () => "Запрошення до компанії прийнято",
    (n) => serializedBody(n) || "Учасник приєднався до компанії.",
  ),
  "company.invitation.declined": entry(
    NOTIFICATION_DOMAIN.COMPANIES,
    (n) => {
      const raw = serializedTitle(n);
      const match = /^Invitation to (.+) declined$/i.exec(raw || "");
      const name = match ? boundedText(match[1]) : "";
      return name
        ? `Запрошення до компанії «${name}» відхилено`
        : "Запрошення до компанії відхилено";
    },
    (n) => serializedBody(n) || "Учасник відхилив запрошення.",
  ),
  "company.join_request.received": entry(
    NOTIFICATION_DOMAIN.COMPANIES,
    (n) => {
      const company = companyFromJoinRequestTitle(n);
      return company
        ? `Запит на приєднання до «${company}»`
        : "Новий запит на приєднання до компанії";
    },
    (n) => serializedBody(n) || "Перегляньте запит у налаштуваннях компанії.",
  ),
  "company.join_request.approved": entry(
    NOTIFICATION_DOMAIN.COMPANIES,
    () => "Запит на приєднання схвалено",
    (n) => {
      const company = extractAfterExactPrefix(serializedTitle(n), "You joined ");
      return company
        ? `Вас додано до компанії «${company}».`
        : serializedBody(n) || "Вас додано до компанії.";
    },
  ),
  "company.join_request.rejected": entry(
    NOTIFICATION_DOMAIN.COMPANIES,
    () => "Запит на приєднання відхилено",
    (n) => serializedBody(n) || "Запит на приєднання до компанії відхилено.",
  ),
  "company.ownership.transferred": entry(
    NOTIFICATION_DOMAIN.COMPANIES,
    () => "Передано власність компанії",
    (n) => {
      const company = extractAfterExactPrefix(
        serializedTitle(n),
        "You are now the owner of ",
      );
      return company
        ? `Ви тепер власник компанії «${company}».`
        : serializedBody(n) || "Власність компанії змінено.";
    },
  ),
  "client.shared": entry(
    NOTIFICATION_DOMAIN.CLIENTS,
    () => "З вами поділилися клієнтом",
    (n) => serializedBody(n) || "Клієнта додано до спільного доступу.",
  ),
  "client.new_match": entry(
    NOTIFICATION_DOMAIN.CLIENTS,
    (n) => {
      const client = extractAfterExactPrefix(serializedTitle(n), "New match for ");
      return client ? `Новий збіг для «${client}»` : "Новий збіг для клієнта";
    },
    (n) =>
      withAggregate(
        "Знайдено обʼєкт, що відповідає уподобанням клієнта.",
        n,
      ),
  ),
  "property.liked": entry(
    NOTIFICATION_DOMAIN.PROPERTY,
    () => "Ваш обʼєкт вподобали",
    (n) =>
      withAggregate("Хтось поставив уподобання вашому обʼєкту.", n),
  ),
  "property.bookmarked": entry(
    NOTIFICATION_DOMAIN.PROPERTY,
    () => "Ваш обʼєкт збережено",
    (n) =>
      withAggregate("Хтось додав ваш обʼєкт до збережених.", n),
  ),
  "property.view_milestone": entry(
    NOTIFICATION_DOMAIN.PROPERTY,
    () => "Новий рубіж переглядів обʼєкта",
    (n) =>
      withAggregate("Ваш обʼєкт набрав черговий рубіж переглядів.", n),
  ),
  "property.share.viewed": entry(
    NOTIFICATION_DOMAIN.PROPERTY,
    () => "Переглянуто поширене посилання",
    (n) =>
      withAggregate(
        "Хтось відкрив поширене посилання на ваш обʼєкт.",
        n,
      ),
  ),
  "viewing.scheduled": entry(
    NOTIFICATION_DOMAIN.VIEWINGS,
    () => "Заплановано перегляд",
    (n) => {
      const payload = safePayload(n);
      const when = formatUkrainianDateTime(payload.scheduled_at);
      const notes = serializedBody(n);
      return joinSupportingParts([
        when ? `Час: ${when}` : "Новий перегляд додано до розкладу.",
        notes,
      ]);
    },
  ),
  "viewing.reminder": entry(
    NOTIFICATION_DOMAIN.VIEWINGS,
    () => "Нагадування про перегляд",
    (n) => {
      const payload = safePayload(n);
      const when = formatUkrainianDateTime(payload.scheduled_at);
      return joinSupportingParts([
        when ? `Час: ${when}` : "Незабаром запланований перегляд.",
        serializedBody(n),
      ]);
    },
  ),
  "viewing.cancelled": entry(
    NOTIFICATION_DOMAIN.VIEWINGS,
    () => "Перегляд скасовано",
    (n) => serializedBody(n) || "Запланований перегляд скасовано.",
  ),
  "deal.created": entry(
    NOTIFICATION_DOMAIN.DEALS,
    () => "Створено нову угоду",
    () => "Нову угоду додано до робочого простору.",
  ),
  "deal.status_changed": entry(
    NOTIFICATION_DOMAIN.DEALS,
    () => "Статус угоди змінено",
    (n) => {
      const payload = safePayload(n);
      const next =
        typeof payload.new_status === "string" ? payload.new_status.trim() : "";
      if (!next || !Object.prototype.hasOwnProperty.call(DEAL_STATUS_LABELS, next)) {
        return "Статус угоди оновлено.";
      }
      return `Новий статус: ${DEAL_STATUS_LABELS[next]}`;
    },
  ),
  "task.assigned": entry(
    NOTIFICATION_DOMAIN.TASKS,
    (n) => {
      const taskTitle = serializedTitle(n);
      return taskTitle || "Вам призначено задачу";
    },
    (n) => {
      const taskTitle = serializedTitle(n);
      const description = serializedBody(n);
      return joinSupportingParts([
        taskTitle ? "Вам призначено цю задачу." : "Нову задачу додано до вашого списку.",
        description,
      ]);
    },
  ),
  "task.due_soon": entry(
    NOTIFICATION_DOMAIN.TASKS,
    (n) => serializedTitle(n) || "Наближається дедлайн задачі",
    (n) =>
      joinSupportingParts([
        "Перевірте задачу з найближчим терміном.",
        serializedBody(n),
      ]),
  ),
  "complaint.filed_against_you": entry(
    NOTIFICATION_DOMAIN.COMPLAINTS,
    () => "Подано скаргу щодо вас",
    (n) => serializedBody(n) || "Скаргу надіслано на розгляд модерації.",
  ),
  "complaint.filed_against_property": entry(
    NOTIFICATION_DOMAIN.COMPLAINTS,
    () => "Подано скаргу щодо обʼєкта",
    (n) =>
      serializedBody(n) || "Скаргу щодо вашого обʼєкта надіслано на розгляд.",
  ),
  "complaint.resolved": entry(
    NOTIFICATION_DOMAIN.COMPLAINTS,
    (n) => {
      const category = extractAfterExactPrefix(
        serializedTitle(n),
        "Complaint resolved: ",
      );
      if (category) return `Скаргу розглянуто: ${category}`;
      const rejected = extractAfterExactPrefix(
        serializedTitle(n),
        "Complaint rejected: ",
      );
      return rejected ? `Скаргу відхилено: ${rejected}` : "Скаргу розглянуто";
    },
    (n) => serializedBody(n) || "Рішення щодо скарги доступне в Рієстро.",
  ),
  "complaint.moderation_pending": entry(
    NOTIFICATION_DOMAIN.COMPLAINTS,
    (n) => {
      const category = extractAfterExactPrefix(
        serializedTitle(n),
        "New complaint: ",
      );
      return category ? `Скарга очікує модерації: ${category}` : "Скарга очікує модерації";
    },
    (n) => serializedBody(n) || "Скаргу передано на модерацію.",
  ),
  "subscription.expiring": entry(
    NOTIFICATION_DOMAIN.SUBSCRIPTION,
    (n) => {
      // Producer may already send Ukrainian titles.
      const title = serializedTitle(n);
      if (title && !/subscription|expir/i.test(title)) return title;
      return "Підписка незабаром закінчиться";
    },
    (n) => serializedBody(n) || "Оновіть підписку, щоб зберегти доступ.",
  ),
  "subscription.expired": entry(
    NOTIFICATION_DOMAIN.SUBSCRIPTION,
    (n) => {
      const title = serializedTitle(n);
      if (title && !/subscription|expir/i.test(title)) return title;
      return "Підписку завершено";
    },
    (n) =>
      serializedBody(n) || "Підписка більше не активна.",
  ),
  "chat.message": entry(
    NOTIFICATION_DOMAIN.CHAT,
    (n) => {
      const count = safeAggregateCount(safePayload(n));
      if (count && count > 1) {
        return `Нові повідомлення (${count})`;
      }
      return "Нове повідомлення";
    },
    (n) =>
      withAggregate("У чаті є непрочитані повідомлення.", n),
  ),
  "user.followed": entry(
    NOTIFICATION_DOMAIN.PROFILE,
    () => "Новий підписник",
    (n) => {
      const name = followerSafeDisplayName(n && n.sender);
      if (name) {
        return `${name} підписався на вас.`;
      }
      return FOLLOWER_GENERIC_BODY;
    },
  ),
  generic: entry(
    NOTIFICATION_DOMAIN.GENERIC,
    () => "Нове сповіщення",
    () => FALLBACK_BODY,
  ),
};

export const ALL_NOTIFICATION_TYPES = Object.keys(
  NOTIFICATION_PRESENTATION_BY_TYPE,
);

/** @deprecated use ALL_NOTIFICATION_TYPES */
export const ALL_PRODUCTION_NOTIFICATION_TYPES = ALL_NOTIFICATION_TYPES;

function resolveEntry(type) {
  if (typeof type === "string" && NOTIFICATION_PRESENTATION_BY_TYPE[type]) {
    return NOTIFICATION_PRESENTATION_BY_TYPE[type];
  }
  return NOTIFICATION_PRESENTATION_BY_TYPE.generic;
}

/**
 * Build safe Ukrainian presentation for a notification row.
 * Does not invent action URLs or private facts.
 */
export function presentNotification(notification) {
  const type =
    notification && typeof notification.type === "string"
      ? notification.type
      : "generic";
  const entryForType = resolveEntry(type);
  const visuals = DOMAIN_VISUALS[entryForType.domain] || DOMAIN_VISUALS.generic;
  const dead = Boolean(notification && notification.dead);
  const isKnownType = Boolean(NOTIFICATION_PRESENTATION_BY_TYPE[type]);

  let title = entryForType.title(notification);
  let body = entryForType.body(notification);

  if (!isKnownType) {
    title = "Нове сповіщення";
    body = FALLBACK_BODY;
  }

  if (dead) {
    body = DEAD_BODY;
  }

  if (!title || typeof title !== "string") {
    title = "Нове сповіщення";
  }
  if (!body || typeof body !== "string") {
    body = FALLBACK_BODY;
  }

  return {
    type,
    domain: entryForType.domain,
    domainLabel: visuals.label,
    iconClass: visuals.icon,
    tone: visuals.tone,
    title: boundedText(title) || "Нове сповіщення",
    body: boundedText(body) || FALLBACK_BODY,
    isKnownType,
  };
}
